import { useState, useEffect } from "react";
import { deleteHotel, fetchHotels as fetchHotelsApi, getHotel, type Hotel as HotelDetail, type HotelListItem } from "@/lib/hotelsApi";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2, Eye, Search, ArrowUpDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useDebounce } from "@/hooks/useDebounce";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import HotelForm from "./HotelForm";
import RoomViewModal from "./RoomViewModal";

type Hotel = HotelListItem;

type SortField = "hotel_name" | "location" | "stars" | null;
type SortOrder = "asc" | "desc";

const HotelsList = () => {
  const { userRole } = useAuth();
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState<HotelDetail | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState(false);
  const [roomModalOpen, setRoomModalOpen] = useState(false);
  const [selectedRoomId, setSelectedRoomId] = useState<number | null>(null);
  const [selectedHotelId, setSelectedHotelId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [starFilter, setStarFilter] = useState<string>("all");
  
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const { toast } = useToast();
  
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const fetchHotels = async () => {
    try {
      setLoading(true);
      const payload = await fetchHotelsApi({
        page: currentPage,
        size: pageSize,
        search: debouncedSearchTerm || undefined,
        sortField,
        sortOrder,
        stars: starFilter === "all" ? undefined : parseInt(starFilter, 10),
      });

      setHotels(payload.items || []);
      setTotalCount(payload.total || 0);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHotels();
  }, [debouncedSearchTerm, currentPage, pageSize, sortField, sortOrder, starFilter]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else {
        setSortField(null);
        setSortOrder("asc");
      }
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const handleView = async (hotel: Hotel) => {
    try {
      const hotelData = await getHotel(hotel.id);
      setSelectedHotel(hotelData);
      setViewMode(true);
      setIsFormOpen(true);
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleEdit = async (hotel: Hotel, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const hotelData = await getHotel(hotel.id);
      setSelectedHotel(hotelData);
      setViewMode(false);
      setIsFormOpen(true);
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteHotel(deleteId);
      toast({ title: "Success", description: "Hotel deleted successfully" });
      fetchHotels();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDeleteId(null);
    }
  };

  const handleViewRoom = (roomId: number, hotelId: number) => {
    setSelectedRoomId(roomId);
    setSelectedHotelId(hotelId);
    setRoomModalOpen(true);
  };

  const canEdit = Boolean(userRole);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-foreground">Hotels</h2>
        {canEdit && (
          <Button onClick={() => { setSelectedHotel(null); setViewMode(false); setIsFormOpen(true); }}>
            <Plus className="mr-2 h-4 w-4" />
            Add Hotel
          </Button>
        )}
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search by hotel name or location..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="pl-10"
          />
        </div>
        <Select value={starFilter} onValueChange={(value) => { setStarFilter(value); setCurrentPage(1); }}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Filter by stars" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stars</SelectItem>
            <SelectItem value="3">3 Stars</SelectItem>
            <SelectItem value="4">4 Stars</SelectItem>
            <SelectItem value="5">5 Stars</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">Image</TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  onClick={() => handleSort("hotel_name")}
                  className="h-8 px-2"
                >
                  Hotel Name
                  <ArrowUpDown 
                    className={`ml-2 h-4 w-4 ${sortField === "hotel_name" ? "text-primary" : ""}`} 
                  />
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  onClick={() => handleSort("location")}
                  className="h-8 px-2"
                >
                  Location
                  <ArrowUpDown 
                    className={`ml-2 h-4 w-4 ${sortField === "location" ? "text-primary" : ""}`} 
                  />
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  onClick={() => handleSort("stars")}
                  className="h-8 px-2"
                >
                  Stars
                  <ArrowUpDown 
                    className={`ml-2 h-4 w-4 ${sortField === "stars" ? "text-primary" : ""}`} 
                  />
                </Button>
              </TableHead>
              <TableHead>Rooms</TableHead>
              {canEdit && (
                <TableHead className="text-right">Actions</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={canEdit ? 6 : 5} className="text-center text-muted-foreground h-24">
                  Loading hotels...
                </TableCell>
              </TableRow>
            ) : hotels.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canEdit ? 6 : 5} className="text-center text-muted-foreground h-24">
                  No hotels found
                </TableCell>
              </TableRow>
            ) : (
              hotels.map((hotel) => (
                <TableRow 
                  key={hotel.id}
                  onClick={() => handleView(hotel)}
                  className="cursor-pointer"
                >
                  <TableCell>
                    {hotel.image_url ? (
                      <img 
                        src={hotel.image_url} 
                        alt={hotel.hotel_name}
                        className="w-16 h-16 object-cover rounded-md"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-muted rounded-md flex items-center justify-center">
                        <span className="text-muted-foreground text-xs">No image</span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">{hotel.hotel_name}</TableCell>
                  <TableCell>{hotel.location || "-"}</TableCell>
                  <TableCell>{"⭐".repeat(hotel.stars || 0)}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {hotel.rooms?.map((room) => (
                        <Button
                          key={room.id}
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewRoom(room.id, hotel.id);
                          }}
                        >
                          <Eye className="mr-1 h-3 w-3" />
                          {room.room_name}
                        </Button>
                      ))}
                    </div>
                  </TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => handleEdit(hotel, e)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteId(hotel.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
            <SelectTrigger className="w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
          <div className="text-sm text-muted-foreground">
            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} records
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          <div className="text-sm">
            Page {currentPage} of {totalPages || 1}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {isFormOpen && (
        <HotelForm
          open={isFormOpen}
          onOpenChange={(open) => {
            if (!open) {
              setIsFormOpen(false);
              setSelectedHotel(null);
            }
          }}
          initialData={selectedHotel}
          viewMode={viewMode}
          onSuccess={() => {
            setIsFormOpen(false);
            setSelectedHotel(null);
            fetchHotels();
          }}
        />
      )}

      {roomModalOpen && selectedRoomId && selectedHotelId && (
        <RoomViewModal
          roomId={selectedRoomId}
          hotelId={selectedHotelId}
          open={roomModalOpen}
          onOpenChange={(open) => {
            if (!open) {
              setRoomModalOpen(false);
              setSelectedRoomId(null);
              setSelectedHotelId(null);
            }
          }}
          onSuccess={() => {
            setRoomModalOpen(false);
            setSelectedRoomId(null);
            setSelectedHotelId(null);
            fetchHotels();
          }}
        />
      )}

      <AlertDialog open={deleteId !== null} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the hotel and all its rooms.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default HotelsList;
